/*
 * Algolia autocomplete glue, ported from the legacy theme's `snippets/predictive-search.liquid`.
 *
 * The Algolia app draws the panel; these hooks tell it what to draw. Ten of them, all pure
 * app API: the options, the query-rule banner plugin, and the templates for the empty state,
 * the suggestions, the collections, the products grid, the header and the footer. The empty
 * state is the markup of `sections/algolia-autocomplete-default-template.liquid`, read out of
 * its <template> and handed over as-is.
 *
 * The product suggestion is the one template that draws nothing: it returns a placeholder that
 * `algolia-liquid-card.js` fills with the theme's `product-card-standalone`, fetched through the
 * Section Rendering API. The dropdown, the results page and the pre-query panel therefore all show
 * the same card - the pre-query panel already rendered that snippet directly.
 *
 * Two things are deliberately not ported. The panel's positioning and close-button chrome was
 * written against the legacy header (`.js-search-header`, `.site-header--stuck`, a `.toolbar`
 * offset, a `.predictive__screen` overlay) and none of it exists here; it is also the part the
 * design review will rewrite. And the data attributes it reads now sit on this theme's search
 * form rather than on a `<predictive-search>` element of the old theme's own.
 *
 * A third thing was ported and has since been taken back out: the legacy theme overwrote
 * `algoliaShopify.config.index_suffix` from a per-market metafield. The app resolves the index for
 * the market being browsed itself, through `config.markets_mapping`, and its answer disagreed with
 * ours - for Italy in English the mapping gives an empty suffix, the shop's default products
 * index, while the theme computed `_italy_en`. Writing ours won whenever this file loaded, so the
 * results page queried a different index depending on whether the autocomplete happened to be
 * switched on. The theme now has no opinion about index names.
 */
(function () {
  // The form `snippets/search-modal.liquid` renders. It carries the placeholder and the
  // translations metafield - what Liquid alone can tell this file - and it is what the app mounts
  // the autocomplete on. Looked up once: the script is deferred, so the document is parsed, and
  // the modal's form is not replaced.
  const searchForm = document.querySelector('form[data-search="algolia"]');
  const autocompletePlaceholder = searchForm?.getAttribute('data-placeholder');

  // The pre-query panel's markup, from the `<template>` the "Algolia Autocomplete" section renders
  // into the header group. Empty string and not `undefined` when that section is missing: this
  // value's only reader hands it to `innerHTML`, and `innerHTML` is annotated
  // `[LegacyNullToEmptyString]`, so only `null` becomes an empty string while `undefined` becomes
  // the six letters of the word. With `openOnFocus` the panel opens on every focus, so that would
  // have read "undefined" in the dropdown each time a shopper clicked the field.
  //
  // An empty panel is still a broken configuration - the setting's `info` names the section as
  // required - so it says so in the console rather than failing quietly. Falling back to the app's
  // own default panel was the alternative and it does not fit: of the four places the value is
  // used, one interpolates it into the no-results markup rather than returning it, and the main
  // template would have to return the app default explicitly because falling through builds the
  // results panel for an empty query. Four behaviours for one misconfiguration, against one empty
  // panel and a message.
  const NL_Algolia_default_template_section = document.querySelector('#nl__aa-Default-Template-template');
  let NL_Algolia_default_template = '';

  if (NL_Algolia_default_template_section) {
    NL_Algolia_default_template = NL_Algolia_default_template_section.innerHTML;
  } else {
    console.warn(
      '[Algolia] The "Algolia Autocomplete" section is missing from the header group, so the panel shown before a shopper types will be empty.',
    );
  }

  // Coming back from a quick add opened inside the search. The app unmounts its panel when the
  // quick add takes over, and the pre-query panel only draws while the autocomplete reports
  // itself open, so with an empty query the shopper returns to a search box with nothing under
  // it. With a query typed the app finds its way back on its own, which is why this was only
  // ever visible on the default panel.
  //
  // Focus is the lever: the app mounts with `openOnFocus`, so giving the field the focus back
  // gives the panel back with it. Deferred a frame because the drawer restores focus as it
  // closes, and the last write is the one that counts.
  //
  // `close` does not bubble, so this listens in the capture phase, which non-bubbling events
  // still pass through on their way down.
  document.addEventListener(
    'close',
    function (event) {
      const dialog = event.target;

      if (!(dialog instanceof HTMLDialogElement) || !dialog.closest('#quick-add-dialog')) return;

      // Only while the search is still standing: a quick add opened from a collection has no
      // search to go back to.
      const searchModal = document.querySelector('dialog-component#search-modal dialog[open]');
      const input = searchModal?.querySelector('.aa-Input, input[type="search"]');

      if (input instanceof HTMLInputElement) requestAnimationFrame(() => input.focus());
    },
    true,
  );

  // An empty query on an open panel is the default state: what the panel shows before anyone types.
  // Every template hook checks this to decide between its results markup and the section's template.
  const stateIsDefault = function (state) {
    return state.query.trim() === '' && state.isOpen === true;
  };

  // The pre-query panel: the markup of `sections/algolia-autocomplete-default-template.liquid`,
  // handed over as-is. Four places want it - three hooks return it for the default state and the
  // no-results template interpolates it into its own markup - so the wrapper is written once, and
  // the value above has exactly one reader.
  //
  // @param {(strings: TemplateStringsArray, ...values: any[]) => any} html
  const defaultPanel = function (html) {
    return html`<div dangerouslySetInnerHTML=${{ __html: NL_Algolia_default_template }} />`;
  };

  // Fills the suggestion placeholders with their Liquid cards. Debounced by
  // `AlgoliaHelpers.hydrateWhenIdle`, which the results page shares, and called from the state
  // change rather than from the product template: the template runs once per suggestion, so the
  // debounce was collapsing a dozen calls per render where one would do.
  const hydratePanelCards = function () {
    window.AlgoliaHelpers.hydrateWhenIdle(document.querySelector('.aa-Panel'));
  };

  // A query that comes back with products but with neither suggestions nor collections is drawn by a
  // template of the app's own, and that wrapper arrives without the theme's page-grid classes: the
  // panel leaves the grid's centre column and everything in it changes size with it.
  //
  // Neither native route reaches it. `classNames.panelLayout` does not - that wrapper is the app's
  // Horizon-mode markup rather than an autocomplete-js primitive, the same reason
  // `.aa-MainContainer` has no key - and no template hook covers that path either. So the classes go
  // on from the outside, keyed on the node's insertion: the panel is rendered after the state change
  // that announced it, sometimes more than a frame later, so a `requestAnimationFrame` pass finds
  // nothing and the classes would land only on the next keystroke. Watching childList alone cannot
  // loop on the class it adds.
  const applyThemePanelLayout = function () {
    for (const layout of document.querySelectorAll('.aa-Panel .aa-PanelLayout:not(.section)')) {
      layout.classList.add('section', 'section--page-width');
    }
  };

  /** @type {MutationObserver | null} */
  let panelLayoutObserver = null;

  const observeThemePanelLayout = function () {
    if (panelLayoutObserver) return;

    const modal = document.querySelector('.search-modal');

    if (!modal) return;

    // Insertions only, and only ones that added something: each card's own `innerHTML` write while
    // it hydrates is a childList mutation of this subtree too, so without the guard a first panel
    // of eight uncached hits ran the document-wide query below eight more times.
    panelLayoutObserver = new MutationObserver(function (records) {
      if (records.some((record) => record.addedNodes.length > 0)) applyThemePanelLayout();
    });

    panelLayoutObserver.observe(modal, { childList: true, subtree: true });
  };

  // The app draws its own magnifier in the form it mounts; the theme's animated one replaces it.
  // Shared with the results page, which does the same to its own search box.
  //
  // The spinner goes in beside it, hidden until the shopper leaves the search for another page:
  // see `showNavigating`.
  const adoptThemeSubmitIcon = function () {
    const button = document.querySelector('.aa-SubmitButton');

    window.AlgoliaHelpers.adoptThemeSearchIcon(button);

    const template = document.querySelector('#search-loading-icon-template');

    if (button && template instanceof HTMLTemplateElement && !button.querySelector('.search-modal__loading-icon')) {
      button.append(template.content.cloneNode(true));
    }
  };

  // Set once the shopper has left for another page from inside the search - a submit, a result,
  // a suggestion - until that page replaces this one. Each of those closes the app's panel first,
  // and the page behind it takes a while to arrive: closing the modal with the panel left the
  // shopper on the page they started from, with nothing saying anything was on its way. The modal
  // stays up instead, with a spinner in place of the magnifier.
  //
  // Only a submit keeps the query showing, because a submit is loading that query. Anything picked
  // from the panel is loading something else, and the old keyword spinning beside it reads as its
  // search still being on the way. The attribute's value tells the two apart and the stylesheet
  // hides the field's text for a pick: emptying the field does not hold, because picking an item
  // writes that item's own text into it a render later, and the app's `setQuery('')` would run a
  // search for the empty query and redraw the panel the shopper just left.
  let navigating = false;

  /** @param {'submit' | 'pick'} source */
  const showNavigating = function (source) {
    navigating = true;

    const modal = document.querySelector('.search-modal');
    modal?.setAttribute('data-navigating', source);

    const status = modal?.querySelector('[data-search-status]');
    if (status instanceof HTMLElement) status.textContent = status.dataset.loadingText ?? '';
  };

  // A click on a link in the panel that loads a page in this tab. Results and suggestions are the
  // app's items, which it answers by closing the panel; the links of the pre-query panel are plain
  // markup and leave it open, but the wait for the page is the same, so they get the spinner too.
  // Read in the bubble phase so a link some script handles itself (`defaultPrevented`) is not one.
  document.addEventListener('click', function (event) {
    if (event.defaultPrevented || event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    const link = event.target instanceof Element ? event.target.closest('a[href]') : null;

    if (!(link instanceof HTMLAnchorElement) || !link.closest('.search-modal .aa-Panel')) return;
    if ((link.target && link.target !== '_self') || link.hasAttribute('download')) return;
    if (!/^https?:$/.test(link.protocol)) return;

    // A fragment of this same page scrolls instead of loading anything.
    if (link.hash && link.href.split('#')[0] === location.href.split('#')[0]) return;

    showNavigating('pick');
  });

  // Back to this page through the back/forward cache, it comes back exactly as it was left: the
  // modal open and spinning on a navigation that already happened. It closes, as it did before the
  // busy state existed.
  window.addEventListener('pageshow', function (event) {
    if (!event.persisted || !navigating) return;

    navigating = false;

    const modal = document.querySelector('.search-modal');
    modal?.removeAttribute('data-navigating');

    const status = modal?.querySelector('[data-search-status]');
    if (status) status.textContent = '';

    const searchDialog = /** @type {any} */ (document.querySelector('dialog-component#search-modal'));
    if (typeof searchDialog?.closeDialog === 'function') searchDialog.closeDialog();
  });

  document.addEventListener('algolia.hooks.initialize', function () {
    algoliaShopify.hooks.registerHook('beforeAutocompleteOptions', function (options) {
      const algolia = window.algoliaShopify;

      // Same helper the results page uses, rather than a second copy of the parse here: the
      // metafield is a shop-owned string, so it has to be read inside a try/catch - unset it
      // serialises to `null`, and one apostrophe in a value makes it invalid JSON. Throwing here
      // would take the placeholder, the panel's own close and the banner plugin down with it.
      window.AlgoliaHelpers.mergeTranslations(options);

      // Customize options here
      options.focus = true; // ToDo: Might be better implementation! Check why focus:true is not working
      options.openOnFocus = true;

      // Appended to the classes the app already puts on its own elements, which is the only way to
      // reach them. Elements this file renders itself carry their classes inline instead.
      options.classNames = {
        ...options.classNames,
        inputWrapperSuffix: 'body2--typography',
      };

      // The app's own submit is the navigation to the results page.
      const oldOnSubmit = options.onSubmit;
      options.onSubmit = function (params) {
        showNavigating('submit');
        if (typeof oldOnSubmit === 'function') oldOnSubmit(params);
      };

      // Picking a result or a suggestion with the keyboard: the item's URL, opened in this tab.
      // Only `navigate` - the new-tab and new-window variants leave this page where it is.
      const oldNavigator = options.navigator;
      options.navigator = {
        ...oldNavigator,
        /** @param {any} params */
        navigate(params) {
          showNavigating('pick');

          if (typeof oldNavigator?.navigate === 'function') oldNavigator.navigate(params);
          else window.location.assign(params.itemUrl);
        },
      };

      // The app owns the outside-click detection and answers it by unmounting its panel, which on
      // its own leaves the modal open and empty. The dialog follows that decision instead of
      // second-guessing where its own outside is.
      const oldOnStateChange = options.onStateChange;
      /** @param {any} params */
      options.onStateChange = function (params) {
        adoptThemeSubmitIcon();
        observeThemePanelLayout();
        applyThemePanelLayout();
        hydratePanelCards();

        if (params.prevState.isOpen && !params.state.isOpen) {
          // Not when a quick add is what displaced the panel. The app reads anything outside its
          // own panel as a dismissal, and a quick add opening over it qualifies - but the shopper
          // has not left the search, they are one panel deep in it, and closing the modal under
          // them means coming back to the page instead of to the results they were reading.
          const quickAddOpen = document.querySelector('#quick-add-dialog dialog[open]');

          // Nor when the panel closed because the shopper is leaving for another page: the modal
          // waits for it. Decided a task later, because the panel closes before anything says so -
          // a picked item calls `onSelect` ahead of the navigator, and a clicked link is only known
          // to load a page once its click has finished bubbling.
          if (!quickAddOpen) {
            setTimeout(function () {
              if (navigating) return;

              const searchDialog = /** @type {any} */ (document.querySelector('dialog-component#search-modal'));

              if (typeof searchDialog?.closeDialog === 'function') searchDialog.closeDialog();
            });
          }
        }

        if (typeof oldOnStateChange === 'function') oldOnStateChange(params);
      };

      if (autocompletePlaceholder) options.placeholder = autocompletePlaceholder;

      /**
       * The banners a query rule offers, as items the panel can render.
       *
       * @param {string} query
       * @returns {Promise<Array<any>>}
       */
      const fetchBanners = async function (query) {
        // The app's own index for the market being browsed, not one rebuilt from the shop
        // handle: the suffix is what the app's markets mapping resolved, and it can be a
        // promise - which is why the app awaits it too.
        const indexSuffix = (await algolia.config.index_suffix) ?? '';

        const results = await algolia.searchClient.search([
          {
            indexName: `${algolia.config.index_prefix}products${indexSuffix}`,
            query,
            params: {
              hitsPerPage: 0,
              clickAnalytics: false,
              ruleContexts: ['autocomplete-banner'],
            },
          },
        ]);

        const renderingContent = results.results[0]?.renderingContent;
        const banners = renderingContent?.widgets?.banners;

        if (!banners || banners.length === 0) return [];

        // Return banners as synthetic items so Autocomplete can render them
        return banners.map((banner, i) => ({
          __autocomplete_id: `banner-${i}`,
          imageUrl: banner.image?.urls[0]?.url ?? null,
          linkUrl: banner.link?.url ?? null,
        }));
      };

      /*
       * This source is asked for items on every state change, and each ask is a search of its own
       * - the plugin does not go through the batched requester the app's own sources share. One
       * per distinct query, then, held for as long as the page lives: a query rule's banner cannot
       * change between two renders of the same query.
       */
      /** @type {Map<string, Promise<Array<any>>>} */
      const bannersByQuery = new Map();

      const bannerPlugin = {
        getSources({ query }) {
          return [
            {
              sourceId: 'queryRuleBanner',
              async getItems() {
                // Nothing to ask about, and nowhere to show it: an empty query draws the default
                // panel, which returns before it ever reads this source. `openOnFocus` made that
                // a billed search thrown away on every single open of the modal.
                if (!query) return [];

                const known = bannersByQuery.get(query);
                if (known) return known;

                // Kept only once it resolves. A cached rejection would leave the query without a
                // banner for the rest of the visit over one failed request.
                const items = fetchBanners(query).catch((error) => {
                  bannersByQuery.delete(query);
                  throw error;
                });

                bannersByQuery.set(query, items);

                return items;
              },
              templates: {
                item({ item, html }) {
                  if (!item.imageUrl) return null;

                  const wrapper = item.linkUrl
                    ? html`<a href=${item.linkUrl} class="aa-BannerLink">
                        <img src=${item.imageUrl} class="aa-BannerImage" alt="Banner" />
                      </a>`
                    : html`<div class="aa-Banner">
                        <img src=${item.imageUrl} class="aa-BannerImage" alt="Banner" />
                      </div>`;

                  return wrapper;
                },
              },
            },
          ];
        },
      };

      options.plugins.push(bannerPlugin);

      return options;
    });

    /*
     * The one index name the theme still has to state. The app derives it as
     * `index_prefix + 'products_query_suggestions'`, which 404s on this store - the console says
     * `Index shopify_sebago-it_products_query_suggestions does not exist` - because the query
     * suggestions indices here are per language and carry no `products_` segment. Without this the
     * dropdown shows products and no suggestions at all.
     *
     * Only this name: the products index is the app's own business, resolved through its markets
     * mapping, and the theme no longer touches it. Built from `config.index_prefix` rather than
     * from the shop handle so there is one source for the prefix, and set in this hook because that
     * is where it has always been early enough for the autocomplete bundle to read it.
     *
     * The durable fix is app-side - either the suggestions indices are renamed to the convention
     * the app expects, or the app is configured with the ones that exist - at which point this can
     * go.
     */
    algoliaShopify.hooks.registerHook('beforeAutocompleteAsyncFunction', async function () {
      const config = window.algoliaShopify.config;
      const language = window.algoliaShopify.shopify.market.language.shop_locale.locale;

      config.query_suggestions_index_name = `${config.index_prefix}query_suggestions_${language}`;
    });

    algoliaShopify.hooks.registerHook(
      'beforeAutocompleteMainTemplate',
      function (_defaultTemplate, templateOptions, elements, displaySuggestions) {
        const { html, state } = templateOptions;

        // The panel before anyone types.
        if (stateIsDefault(state)) return defaultPanel(html);

        // A query can come back with products and nothing for the left column. The panel is a flex
        // row whose left is sized by its content, so an empty one still claims the row's gap and
        // leaves the products short of the full width: it is not rendered at all instead.
        const hasSideColumn = Boolean(
          displaySuggestions || elements.collections || elements.pages || elements.redirectUrlPlugin,
        );

        return html`
          <div class="aa-PanelLayout aa-Panel--scrollable section section--page-width">
            <div class="aa-PanelSections">
              ${
                hasSideColumn &&
                html`
                  <div class="aa-PanelSection--left">
                    ${
                      displaySuggestions &&
                      html`
                        <div class="nl__aa-PanelSection__group">
                          <div class="aa-SourceHeader">
                            <span class="cta1--typography">${algoliaShopify.translations.suggestions}</span>
                          </div>
                          ${elements.querySuggestionsPlugin}
                        </div>
                      `
                    }
                    ${
                      elements.collections &&
                      html`<div class="nl__aa-PanelSection__group">${elements.collections}</div>`
                    }
                    ${elements.pages && html`<div class="nl__aa-PanelSection__group">${elements.pages}</div>`}
                    ${
                      elements.redirectUrlPlugin &&
                      html`<div class="nl__aa-PanelSection__group">${elements.redirectUrlPlugin}</div>`
                    }
                  </div>
                `
              }
              <div class="aa-PanelSection--right">
                ${elements.queryRuleBanner && html`<div class="aa-PanelSection--banners">${elements.queryRuleBanner}</div>`}
                ${elements.products}
              </div>
            </div>
          </div>
        `;
      },
    );

    algoliaShopify.hooks.registerHook(
      'beforeAutocompleteNoResultsTemplate',
      function (_defaultTemplate, templateOptions, elements, displaySuggestions) {
        const { html, state } = templateOptions;

        if (stateIsDefault(state)) return defaultPanel(html);

        return html`
          <div class="aa-PanelLayout aa-Panel--scrollable section section--page-width">
            <div class="aa-no-results">
              <p>${algoliaShopify.translation_helpers.no_result_for(state.query)}</p>
              <a class="button-unstyled size-style" href="${window.Shopify.routes.root}collections/all">
                ${algoliaShopify.translations.allProducts}
              </a>
            </div>
            <div class="aa-no-results--default-template">${defaultPanel(html)}</div>
          </div>
        `;
      },
    );

    algoliaShopify.hooks.registerHook(
      'beforeAutocompleteCollectionsTemplate',
      (_defaultTemplate, templateOptions, itemLink) => {
        const { html, item } = templateOptions;
        return html`
          <a href="${itemLink}" class="aa-ItemLink">
            <div class="aa-ItemWrapper">
              <div class="aa-ItemContent">
                <div class="aa-ItemIcon aa-ItemIcon--noBorder aa-ItemIcon--search"></div>
                <div class="aa-ItemContentBody">
                  <p>${item.title}</p>
                </div>
              </div>
            </div>
          </a>
        `;
      },
    );

    algoliaShopify.hooks.registerHook(
      'beforeAutocompletePagesTemplate',
      (_defaultTemplate, templateOptions, itemLink) => {
        const { html, item } = templateOptions;
        return html`
          <a href="${itemLink}" class="aa-ItemLink aa-ProductItem">
            <div class="aa-ItemWrapper">
              <div class="aa-ItemContent">
                <div class="aa-ItemIcon aa-ItemIcon--noBorder">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
                <div class="aa-ItemContentBody">
                  <p>${item.title}</p>
                </div>
              </div>
            </div>
          </a>
        `;
      },
    );

    algoliaShopify.hooks.registerHook(
      'beforeAutocompleteSuggestionsTemplate',
      function (_defaultTemplate, templateOptions) {
        const { html, item } = templateOptions;

        return html`
          <a
            class="aa-ItemLink aa-ItemWrapper"
            href="${window.Shopify.routes.root}search?q=${encodeURIComponent(item.query)}"
          >
            <div class="aa-ItemContent">
              <div class="aa-ItemIcon aa-ItemIcon--noBorder aa-ItemIcon--search"></div>
              <div class="aa-ItemContentBody">
                <p>${item.query}</p>
              </div>
            </div>
          </a>
        `;
      },
    );

    algoliaShopify.hooks.registerHook(
      'beforeAutocompleteMainProductsTemplate',
      function (_defaultTemplate, templateOptions) {
        const { html, state } = templateOptions;

        // The panel before anyone types. Every other state keeps the app's own grid, which the
        // product template below fills with the theme's cards.
        if (stateIsDefault(state)) return defaultPanel(html);

        return _defaultTemplate;
      },
    );

    algoliaShopify.hooks.registerHook(
      'beforeAutocompleteProductTemplate',
      function (_defaultTemplate, templateOptions, _distinct, itemLink, trackSearchAttribution) {
        const { html, item } = templateOptions;

        /*
         * Same placeholder-and-fill approach as the results grid (see algolia-liquid-card.js): the
         * suggestion is the theme's `product-card-standalone`, so the dropdown and the results page
         * show the same card.
         *
         * Unlike the default template this is NOT wrapped in an `<a>`. The card carries its own
         * links and quick-add controls, and interactive content cannot live inside an anchor - so
         * navigation stays with the card's links and this element only keeps the attribution click.
         */
        const key = window.AlgoliaLiquidCard.cardKey(item.handle, item.objectID);
        const card = window.AlgoliaLiquidCard.getCard(key);

        return html`
          <div
            class="aa-ProductItem algolia-card"
            data-algolia-card
            data-handle="${item.handle}"
            data-variant-id="${item.objectID}"
            data-product-id="${item.id}"
            data-algolia-link="${itemLink}"
            data-algolia-card-key=${card ? key : null}
            onClick="${(e) => {
              // Every control the card offers - the gallery arrows, quick add, a swatch, the
              // wishlist heart - sits inside this element, so its click reaches here too and the
              // app reads that as picking the hit, which closes the panel. Only the card's links
              // and its empty space pick it.
              if (e.target.closest('button, [role="button"], input, label, .bn-wishlist-icon')) {
                e.stopPropagation();
                e.stopImmediatePropagation();
                return;
              }

              trackSearchAttribution(item);
            }}"
            dangerouslySetInnerHTML=${{ __html: card ?? window.AlgoliaLiquidCard.getSkeleton() }}
          ></div>
        `;
      },
    );

    algoliaShopify.hooks.registerHook(
      'beforeAutocompleteHeaderTemplate',
      function (_defaultTemplate, templateOptions, resource) {
        const { html, state } = templateOptions;

        const getViewAllLink = () => {
          if (resource == algoliaShopify.translations.products) {
            return html`<a
              class="button-unstyled size-style"
              href="${window.Shopify.routes.root}search?q=${encodeURIComponent(state.query)}"
            >
              ${algoliaShopify.translations.allProducts}
            </a>`;
          }
        };

        // ToDo: check if resource is translated
        return html`
            <span class="cta1--typography"> ${resource} </span>
            ${getViewAllLink()}
        `;
      },
    );

    // "See all results" lives in the products header instead, next to the resource name.
    algoliaShopify.hooks.registerHook('beforeAutocompleteFooterTemplate', function (_defaultTemplate, templateOptions) {
      return templateOptions.html``;
    });
  });
})();
